import React, { useEffect, useState } from 'react';
import { ShoppingCart, Phone, MapPin, Calendar, User, Search } from 'lucide-react';
import { MessageCircle } from 'lucide-react'; // Using MessageCircle as WhatsApp icon proxy if needed, or stick to text

const BuyProduct = () => {
  const [sellPosts, setSellPosts] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const s = localStorage.getItem('marketplaceSellPosts');
    if (s) setSellPosts(JSON.parse(s));
  }, []);

  const filteredPosts = sellPosts.filter(p =>
    p.type?.toLowerCase().includes(filter.toLowerCase()) ||
    p.otherName?.toLowerCase().includes(filter.toLowerCase()) ||
    p.address?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-transparent py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header with Search */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <h1 className="text-4xl font-black text-slate-800 mb-2">Marketplace</h1>
            <p className="text-slate-600 text-lg">Direct from farm to your doorstep.</p>
          </div>

          <div className="relative w-full md:w-80 group">
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition group-hover:shadow-md"
            />
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-3.5" />
          </div>
        </div>

        {/* Listings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map(p => (
            <div key={p.id} className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 flex flex-col h-full group">

              {/* Image Header */}
              <div className="h-56 bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                {p.image ? (
                  <img src={p.image} alt={p.type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                    <ShoppingCart className="w-16 h-16 mb-2 opacity-50" />
                    <span className="text-sm font-medium">No Image</span>
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-full font-bold text-emerald-700 shadow-sm border border-emerald-100">
                  ₹ {p.price} <span className="text-xs font-normal text-slate-500">/unit</span>
                </div>
                <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs font-medium flex items-center">
                  <Calendar className="w-3 h-3 mr-1.5" />
                  {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'Recent'}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Product</div>
                    <h3 className="text-2xl font-black text-slate-800 leading-none">{p.type === 'Other' ? p.otherName : p.type}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Quantity</div>
                    <div className="text-lg font-bold text-slate-700 bg-slate-50 px-3 py-1 rounded-lg">{p.quantity}</div>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start text-slate-500 text-sm">
                    <MapPin className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-emerald-500" />
                    <span>{p.address}</span>
                  </div>
                  <div className="flex items-center text-slate-500 text-sm">
                    <User className="w-4 h-4 mr-2 shrink-0 text-emerald-500" />
                    <span>Seller ID: {String(p.id).slice(-6)}</span>
                  </div>
                </div>

                <div className="mt-auto grid grid-cols-2 gap-3">
                  {p.phone ? (
                    <a href={`tel:${p.phone}`} className="flex items-center justify-center space-x-2 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-bold transition-colors">
                      <Phone className="w-4 h-4" />
                      <span>Call</span>
                    </a>
                  ) : (
                    <button type="button" disabled className="flex items-center justify-center space-x-2 bg-slate-100 text-slate-400 py-3 rounded-xl font-bold cursor-not-allowed">
                      <Phone className="w-4 h-4" />
                      <span>Call</span>
                    </button>
                  )}
                  {p.phone && (
                    <a href={`https://wa.me/${String(p.phone).replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center justify-center space-x-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-3 rounded-xl font-bold transition-all shadow-lg shadow-green-200">
                      <MessageCircle className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredPosts.length === 0 && (
            <div className="col-span-full py-20 text-center bg-white/50 rounded-3xl border border-dashed border-slate-300">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-slate-600">No products found</h3>
              <p className="text-slate-400">Try adjusting your search terms.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyProduct;
